<script setup lang="ts">
import Lucide from "../../base-components/Lucide";
import {ref} from "vue";
import {ChatMensaje, utilFechaConvertirNumeroString} from "@fixeduy/fixedtypes";

interface Props {
  chat: ChatMensaje;
  mostrarNombre: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "cerrar"): void;
  (e: "cargar"): void;
  (e: "descargar-archivo", url: string, nombre: string): void;
  (e: "show-modal", imagen: any, modal: showModalType): void;
}>();

const descargarArchivo = (url: string, nombre: string) => {
  emit("descargar-archivo", url, nombre);
};

enum showModalType {
  none,
  AbrirImagen
}

const showModal = (imagen: any, modalShow: showModalType) => {
  emit("show-modal", imagen, modalShow);
};

const esEnlace = (texto: string) => {
  // Expresión regular para detectar enlaces en el texto
  const regex = /\b(?:https?|http):\/\/\S+/gi;
  return regex.test(texto);
};
// Función para formatear el mensaje
const formatearMensaje = (mensaje: string) => {
  // Expresión regular para detectar enlaces
  const regex = /(\b(?:https?|http):\/\/\S+)/gi;

  // Reemplaza los enlaces con un HTML de enlace
  return mensaje.replace(regex, match => {
    return `<a href="${match}" target="_blank" style="color: cornflowerblue;">${match}</a>`;
  });
};

const esImagen = (nombreAdjunto: string) => {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "avif"];
  const extension = nombreAdjunto.split(".").pop()?.toLowerCase() ?? "";
  return imageExtensions.includes(extension);
};
</script>

<template>
  <div
    v-if="props.chat.enviadoPorFixed"
    class="flex items-end mb-1 pb-1 break-words"
    :title="utilFechaConvertirNumeroString(props.chat.fecha, true, '/')"
  >
    <div
      v-if="props.chat.urlImagenRemitente && (props.chat.refAdjunto != '' || props.chat.mensaje != '')"
      class="relative flex-none w-6 h-6 mr-2 sm:block image-fit"
    >
      <div
        v-if="props.mostrarNombre"
        class="absolute -top-8 bg-gray-700 text-white text-xs rounded p-1 w-[100px] z-10 sm:hidden"
      >
        {{ props.chat.autorNombre }}
      </div>
      <img
        alt="Foto perfil"
        class="rounded-full"
        :src="props.chat.urlImagenRemitente"
        :title="props.chat.autorNombre"
      />
    </div>
    <div
      class="px-2 py-1 bg-slate-100 dark:bg-darkmode-400 text-slate-700 dark:text-slate-300 rounded-r-md rounded-t-md"
      v-if="props.chat.refAdjunto != '' || props.chat.mensaje != ''"
    >
      <div v-if="esEnlace(props.chat.mensaje)">
        <div v-html="formatearMensaje(props.chat.mensaje)"></div>
        <!-- <a :href="props.chat.mensaje" target="_blank" style="color: cornflowerblue">{{ props.chat.mensaje }}</a> -->
      </div>
      <div v-else style="white-space: pre-line">
        {{ props.chat.mensaje }}
      </div>
      <div v-if="props.chat.refAdjunto != ''" class="text-center">
        <hr style="border-width: 2px" />
        <div v-if="esImagen(props.chat.nombreAdjunto)">
          <img
            @click="showModal(props.chat.urlAdjunto, showModalType.AbrirImagen)"
            :src="props.chat.urlAdjunto"
            style="cursor: pointer; max-height: 150px"
            alt="Adjunto"
          />
        </div>
        <div
          class="mt-2"
          @click="descargarArchivo(props.chat.urlAdjunto, props.chat.nombreAdjunto)"
          style="cursor: pointer"
        >
          <div class="flex">
            <Lucide icon="PaperclipIcon" class="mr-2" />
            <span> {{ props.chat.nombreAdjunto }}</span>
          </div>
          <div class="flex">
            <span v-if="Number(props.chat.tamanioAdjunto) >= 1000000">
              {{ (Number(props.chat.tamanioAdjunto) / 1000000).toFixed(1) }}
              MB |
            </span>
            <span v-else-if="Number(props.chat.tamanioAdjunto) >= 1000">
              {{ (Number(props.chat.tamanioAdjunto) / 1000).toFixed(1) }} KB |
            </span>
            <span v-else> {{ Number(props.chat.tamanioAdjunto) }} bytes | </span>
            <span class="text-blue-500"> Descargar</span>
          </div>
        </div>
      </div>
      <!-- <div class="mt-1 text-xs text-slate-900 text-left">
                {{ utilFechaConvertirNumeroString(props.chat.fecha, true, "/") }}
              </div> -->
    </div>
  </div>
  <div
    v-else
    class="flex justify-end items-end mb-1 pb-1 break-words"
    :title="utilFechaConvertirNumeroString(props.chat.fecha, true, '/')"
  >
    <div
      class="px-2 py-1 text-white bg-primary rounded-l-md rounded-t-md text-right"
      v-if="props.chat.refAdjunto != '' || props.chat.mensaje != ''"
    >
      <div v-if="esEnlace(props.chat.mensaje)">
        <div v-html="formatearMensaje(props.chat.mensaje)"></div>
        <!-- <a :href="props.chat.mensaje" target="_blank" style="color: cornflowerblue">{{ props.chat.mensaje }}</a> -->
      </div>
      <div v-else style="white-space: pre-line">{{ props.chat.mensaje }}</div>
      <div v-if="props.chat.refAdjunto != ''" class="text-center">
        <hr style="border-width: 2px" />
        <div v-if="esImagen(props.chat.nombreAdjunto)">
          <img
            @click="showModal(props.chat.urlAdjunto, showModalType.AbrirImagen)"
            :src="props.chat.urlAdjunto"
            style="cursor: pointer; max-height: 150px"
            alt="Adjunto"
          />
        </div>
        <div
          class="mt-1"
          @click="descargarArchivo(props.chat.urlAdjunto, props.chat.nombreAdjunto)"
          style="cursor: pointer"
        >
          <div class="flex">
            <Lucide icon="PaperclipIcon" class="mr-2" />
            <span> {{ props.chat.nombreAdjunto }}</span>
          </div>
          <div class="flex">
            <span v-if="Number(props.chat.tamanioAdjunto) >= 1000000">
              {{ (Number(props.chat.tamanioAdjunto) / 1000000).toFixed(1) }}
              MB |
            </span>
            <span v-else-if="Number(props.chat.tamanioAdjunto) >= 1000">
              {{ (Number(props.chat.tamanioAdjunto) / 1000).toFixed(1) }} KB |
            </span>
            <span v-else> {{ Number(props.chat.tamanioAdjunto) }} bytes | </span>
            <span class="text-blue-500"> Descargar</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="props.chat.urlImagenRemitente" class="relative flex-none w-6 h-6 ml-2 mb-1 sm:block image-fit">
      <img alt="Foto perfil" class="rounded-full" :src="props.chat.urlImagenRemitente" />
    </div>
  </div>
</template>
<style scoped>
.break-words {
  overflow-wrap: break-word;
  word-break: break-word;
}
.break-all {
  word-break: break-all;
}
</style>
